import React, { useEffect, useState } from "react";
import { api, friendlyError } from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Pill } from "../lib/ui";
import { Plus, Check, Minus, X, Bell } from "lucide-react";
import { toast } from "sonner";

const MARKETS = [
  { code: "eu", label: "EU" }, { code: "usa", label: "USA" }, { code: "japan", label: "JP" },
  { code: "china", label: "CN" }, { code: "korea", label: "KR" }, { code: "taiwan", label: "TW" },
];

export default function Clienti() {
  const [clients, setClients] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", mah: "", markets: [], dossier_type: "", quality_agreement: true,
    notification_days: 30, prior_approval: false, notification_clause: "" });

  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/clients", form); toast.success("Cliente creato"); setShow(false); load(); }
    catch (e) { toast.error(friendlyError(e)); }
  };
  const toggleMkt = (c) => setForm((f) => ({ ...f, markets: f.markets.includes(c) ? f.markets.filter((x) => x !== c) : [...f.markets, c] }));

  return (
    <div className="ri-fade-in">
      <PageHeader overline="Client Regulatory Matrix" title="Clienti"
        subtitle="Matrice clienti × mercati usata automaticamente per gli impact assessment e le notifiche."
        actions={<button onClick={() => setShow(true)} data-testid="client-add-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-700 text-white text-sm hover:bg-blue-800"><Plus size={15} /> Nuovo cliente</button>} />

      <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
        <table className="ri-table">
          <thead><tr>
            <th>Cliente</th><th>MAH</th><th>Dossier</th>
            {MARKETS.map((m) => <th key={m.code} className="text-center">{m.label}</th>)}
            <th>Quality Agreement</th><th>Notifica</th>
          </tr></thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} data-testid={`client-row-${c.id}`}>
                <td className="font-medium">{c.name}{c.is_demo && <span className="ml-2 text-[0.6rem] font-mono px-1 rounded bg-purple-100 text-purple-700">DEMO</span>}</td>
                <td className="text-slate-500">{c.mah}</td>
                <td className="text-slate-500 text-xs">{c.dossier_type}</td>
                {MARKETS.map((m) => (
                  <td key={m.code} className="text-center">
                    {(c.markets || []).includes(m.code)
                      ? <Check size={15} className="text-green-600 mx-auto" />
                      : <Minus size={13} className="text-slate-300 mx-auto" />}
                  </td>
                ))}
                <td>{c.quality_agreement ? <Pill color="#166534" bg="#DCFCE7">Sì</Pill> : <Pill>No</Pill>}</td>
                <td className="text-xs">
                  {c.notification_days ? <span className="inline-flex items-center gap-1"><Bell size={11} /> {c.notification_days}gg</span> : "—"}
                  {c.prior_approval && <span className="block text-[0.65rem] text-orange-600">prior approval</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.6)" }}>
          <div className="bg-white rounded-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head font-semibold text-lg">Nuovo cliente</h3>
              <button onClick={() => setShow(false)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="Nome cliente" data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="MAH" value={form.mah} onChange={(e) => setForm({ ...form, mah: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                <input placeholder="Dossier type" value={form.dossier_type} onChange={(e) => setForm({ ...form, dossier_type: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </div>
              <div>
                <div className="ri-overline mb-1.5">Mercati</div>
                <div className="flex flex-wrap gap-1.5">
                  {MARKETS.map((m) => (
                    <button key={m.code} type="button" onClick={() => toggleMkt(m.code)}
                      className={`px-2.5 py-1 rounded-md text-xs border ${form.markets.includes(m.code) ? "bg-blue-600 text-white border-blue-600" : "border-slate-200"}`}>{m.label}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.quality_agreement} onChange={(e) => setForm({ ...form, quality_agreement: e.target.checked })} /> Quality Agreement</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.prior_approval} onChange={(e) => setForm({ ...form, prior_approval: e.target.checked })} /> Prior approval</label>
              </div>
              <input placeholder="Giorni notifica" type="number" value={form.notification_days} onChange={(e) => setForm({ ...form, notification_days: +e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              <input placeholder="Clausola di notifica (QA §...)" value={form.notification_clause} onChange={(e) => setForm({ ...form, notification_clause: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              <button onClick={create} data-testid="client-save-btn" className="w-full py-2.5 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800">Salva cliente</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
